import { N8N_CALL_CONV_URL } from '@/api/n8n'

// Client próprio, não usa useAdminDB() (aquele é acoplado ao N8N_TEAMS_URL) —
// este recurso roda no workflow isolado "Atribuicao-chamado"
// (n8n/Atribuicao-chamado.json), separado do workflow gigante pra isolar
// debug (ver histórico: os comandos viveram no workflow principal primeiro,
// mudou pra cá por suspeita de conflito/import não pegando lá).
async function post(command: string, extra: Record<string, unknown> = {}): Promise<unknown> {
  const res = await fetch(N8N_CALL_CONV_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ command, timestamp: Date.now(), ...extra }),
  })
  if (!res.ok) throw new Error(`CallConv ${res.status}`)
  const text = await res.text()
  if (!text.trim()) return []
  return JSON.parse(text)
}

/** Ação do agente no popup "Novo atendimento atribuído" — ver migração v36. */
export type CallConvAction = 'atendido' | 'dispensado'

export interface PendingCallConvRow {
  id: number
  conversationId: number
  agentId: number
  agentName: string
  contactName: string
  isManual: boolean
  assignedById: number | null
  assignedByName: string | null
  assignedAt: number // epoch seconds
  /** null enquanto o agente não respondeu ao popup (migração v36). */
  action: CallConvAction | null
  actionAt: number | null // epoch seconds
  /** Hora da 1ª mensagem não-privada do agente pro cliente (migração v36). */
  firstAgentMessageAt: number | null // epoch seconds
}

/**
 * Registra no histórico (tabela call_conv, migração v28) uma atribuição de
 * agente. Por enquanto só a atribuição MANUAL chama isso (ver
 * logManualAssignment em conversationActionsService.ts) — a automática por
 * fluxo ainda vai ficar por dentro do próprio workflow do bot no n8n.
 */
export async function logCallConv(entry: {
  conversationId: number
  agentId: number
  agentName: string
  contactName: string
  isManual: boolean
  assignedById?: number | null
  assignedByName?: string | null
}): Promise<void> {
  await post('log_call_conv', {
    conversation_id:  entry.conversationId,
    agent_id:         entry.agentId,
    agent_name:       entry.agentName,
    contact_name:     entry.contactName,
    is_manual:        entry.isManual ? 1 : 0,
    assigned_by_id:   entry.assignedById ?? null,
    assigned_by_name: entry.assignedByName ?? null,
  })
}

// Linhas de call_conv ainda não dispensadas pra um agente — usado no
// catch-up do popup "Novo atendimento atribuído" (ver loadData em
// useDashboardEngine.ts), pra reabrir o popup mesmo quando a atribuição
// aconteceu enquanto o agente estava offline (sem transição pra detectar
// num diff de polling ao vivo).
export async function getPendingCallConv(agentId: number): Promise<PendingCallConvRow[]> {
  const data = await post('get_pending_call_conv', { agent_id: agentId })
  const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
  return rows.map(rowFromRaw)
}

// Histórico de atribuição de UMA conversa (mais recente primeiro) — usado por
// ResolutionDetailModal.vue pra mostrar se a atribuição atual foi manual (com
// quem atribuiu) ou automática (fluxo/fila, grava com assigned_by_name =
// 'Sistema', ver migrations/admin-mysql-migration-v28.sql). Retorna [] tanto
// pra "sem histórico" quanto pra falha de rede — o chamador não distingue os
// dois casos hoje (só teria dado pra decidir se mostrar "sem dados" vs erro).
export async function getCallConvByConversation(conversationId: number): Promise<PendingCallConvRow[]> {
  const data = await post('get_call_conv_by_conversation', { conversation_id: conversationId })
  const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
  return rows.map(rowFromRaw)
}

function rowFromRaw(r: Record<string, unknown>): PendingCallConvRow {
  return {
    id:                  Number(r.id),
    conversationId:      Number(r.conversation_id),
    agentId:             Number(r.agent_id),
    agentName:           String(r.agent_name ?? ''),
    contactName:         String(r.contact_name ?? ''),
    isManual:            Number(r.is_manual) === 1,
    assignedById:        r.assigned_by_id != null ? Number(r.assigned_by_id) : null,
    assignedByName:      r.assigned_by_name != null ? String(r.assigned_by_name) : null,
    assignedAt:          Number(r.assigned_at),
    action:              r.action === 'atendido' || r.action === 'dispensado' ? r.action : null,
    actionAt:            r.action_at != null ? Number(r.action_at) : null,
    firstAgentMessageAt: r.first_agent_message_at != null ? Number(r.first_agent_message_at) : null,
  }
}

// Marca dispensado (dismissed = 1) por conversa+agente — não pelo id
// numérico da linha, porque o popup pode ter sido aberto tanto pela
// detecção ao vivo (que não conhece o id) quanto pelo catch-up acima (que
// conhece). Casar por conv+agente funciona nos dois casos; é no-op se não
// houver linha pendente pra esse par (ex.: popup de preview do Ctrl+!).
// `action` (migração v36) registra qual dos dois botões do popup foi
// clicado — ver AssignmentAlertModal.vue.
export async function dismissCallConv(conversationId: number, agentId: number, action: CallConvAction): Promise<void> {
  await post('dismiss_call_conv', { conversation_id: conversationId, agent_id: agentId, action })
}

// Registra a 1ª mensagem NÃO-privada do agente pro cliente numa conversa
// (migração v36) — chamado fire-and-forget em useMessageSender.ts a cada
// envio bem-sucedido. Idempotente do lado do banco (UPDATE ... WHERE
// first_agent_message_at IS NULL): repetir a chamada em toda mensagem é
// seguro, sem precisar de estado novo no front pra saber "já é a
// primeira?". É no-op se não houver linha de atribuição pra esse
// agente+conversa (mesmo comportamento já aceito hoje em dismissCallConv).
export async function logFirstAgentMessage(conversationId: number, agentId: number): Promise<void> {
  await post('log_first_agent_message', { conversation_id: conversationId, agent_id: agentId })
}

/** Linha do relatório "Atendimentos" (Admin) — ver AssignmentMetricsSection.vue. */
export interface CallConvMetricRow {
  id: number
  conversationId: number
  agentId: number
  agentName: string
  contactName: string
  isManual: boolean
  assignedByName: string | null
  action: CallConvAction | null
  dismissed: boolean
  assignedAt: number // epoch seconds
  actionAt: number | null // epoch seconds
  firstAgentMessageAt: number | null // epoch seconds
}

// Histórico pra relatório (Admin → Atendimentos), filtrado por período
// (obrigatório — call_conv cresce a cada atribuição automática do bot,
// não só manual) e agente (opcional). Teto de 500 linhas no comando n8n,
// não é paginação real.
export async function listCallConvMetrics(filters: { from: number; to: number; agentId?: number | null }): Promise<CallConvMetricRow[]> {
  const body: Record<string, unknown> = { from: filters.from, to: filters.to }
  if (filters.agentId != null) body.agent_id = filters.agentId
  const data = await post('list_call_conv_metrics', body)
  const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
  return rows.map(r => ({
    id:                  Number(r.id),
    conversationId:      Number(r.conversation_id),
    agentId:             Number(r.agent_id),
    agentName:           String(r.agent_name ?? ''),
    contactName:         String(r.contact_name ?? ''),
    isManual:            Number(r.is_manual) === 1,
    assignedByName:      r.assigned_by_name != null ? String(r.assigned_by_name) : null,
    action:              r.action === 'atendido' || r.action === 'dispensado' ? r.action : null,
    dismissed:           Number(r.dismissed) === 1,
    assignedAt:          Number(r.assigned_at),
    actionAt:            r.action_at != null ? Number(r.action_at) : null,
    firstAgentMessageAt: r.first_agent_message_at != null ? Number(r.first_agent_message_at) : null,
  }))
}
